import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/lib/instrumented-toast";
import { messageFromAxiosOrError } from "@/lib/invite-link";
import { useApiInstance } from "@/providers/api-provider";
import React from "react";

interface RenameOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName: string;
  onSuccess?: () => void;
}

export function RenameOrganizationDialog({
  open,
  onOpenChange,
  organizationId,
  organizationName,
  onSuccess,
}: RenameOrganizationDialogProps) {
  const [orgName, setOrgName] = React.useState(organizationName);
  const [isRenaming, setIsRenaming] = React.useState(false);

  React.useEffect(() => {
    if (open) setOrgName(organizationName);
  }, [open, organizationName]);

  const trimmed = orgName.trim();
  const isUnchanged = trimmed === organizationName;

  const handleRename = async () => {
    if (!trimmed || isUnchanged) return;

    setIsRenaming(true);
    try {
      await useApiInstance.patch(`/api/v1/organizations/${organizationId}`, { name: trimmed });
      toast.success(`Organization renamed to "${trimmed}"`);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(`Failed to rename organization: ${messageFromAxiosOrError(error)}`);
    } finally {
      setIsRenaming(false);
    }
  };

  const handleClose = () => {
    if (!isRenaming) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Rename Organization</DialogTitle>
          <DialogDescription>
            Rename <strong>{organizationName}</strong>. Existing license keys keep the previous name and stay valid. The
            organization switcher may show the old name until users sign in again.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="rename-org-name">New Organization Name</Label>
            <Input
              id="rename-org-name"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Enter organization name..."
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isRenaming) {
                  handleRename();
                }
              }}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isRenaming}>
            Cancel
          </Button>
          <Button onClick={handleRename} disabled={isRenaming || !trimmed || isUnchanged}>
            {isRenaming ? "Renaming..." : "Rename Organization"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
